import type { Locale } from 'next-intl';
import {
    OutageReturnDay,
    OutageReturnMode,
    VenueAvailabilityKind,
    type OutageReturn,
    type VenueAvailabilityStatus,
} from '@/entities/venue/lib/venue-availability.utils';
import type { MessageKey, Translate } from '@/shared/i18n/translate';
import { capitalize } from '@/shared/lib/capitalize';
import { getLocaleDate, getLocaleDateTime, getLocaleTime } from '@/shared/lib/format-locale-date-time.util';
import type { LocalizedString } from '@/shared/model/Language';

type ClosedStatus = Extract<VenueAvailabilityStatus, { kind: VenueAvailabilityKind.Closed }>;

export type ClosedCopyKeys = {
    today: MessageKey;
    tomorrow: MessageKey;
    later: MessageKey;
};

/** Closed opening phrase from dayDiff (navbar / card / cart). */
export const getClosedCopyText = (
    status: ClosedStatus,
    locale: Locale,
    t: Translate,
    keys: ClosedCopyKeys,
): string => {
    const time = getLocaleTime(status.opensAtDayjs.toDate(), locale);
    if (status.dayDiff <= 0) {
        return t(keys.today, { time });
    }
    if (status.dayDiff === 1) {
        return t(keys.tomorrow, { time });
    }
    return t(keys.later, {
        day: capitalize(status.opensAtDayjs.locale(locale.toLowerCase()).format('dddd')),
        date: getLocaleDate(status.opensAtDayjs.toDate(), locale),
        time,
    });
};

export type OutageReturnKeys = {
    minutes: MessageKey;
    soon: MessageKey;
    at: MessageKey;
    tomorrowAt: MessageKey;
    untilDate: MessageKey;
};

/** Outage “back when” phrase (navbar tail / venue card detail). */
export const getOutageReturnText = (
    ret: OutageReturn,
    at: Date | number | string,
    locale: string,
    t: Translate,
    keys: OutageReturnKeys,
): string => {
    switch (ret.mode) {
        case OutageReturnMode.Soon:
            return t(keys.soon);
        case OutageReturnMode.Minutes:
            return t(keys.minutes, { minutes: ret.minutesUntil });
        case OutageReturnMode.AtTime: {
            const time = getLocaleTime(at, locale);
            return ret.day === OutageReturnDay.Tomorrow ? t(keys.tomorrowAt, { time }) : t(keys.at, { time });
        }
        case OutageReturnMode.UntilDate:
            return t(keys.untilDate, { date: getLocaleDateTime(at, locale) });
    }
};

export type OutageWhenKeys = Pick<ClosedCopyKeys, 'today' | 'tomorrow'> &
    Pick<OutageReturnKeys, 'minutes' | 'soon' | 'untilDate'>;

/**
 * Short “when” fragment for cart microcopy / CTA (`today at 17:00`, `tomorrow at 10:02`, …).
 * Works for closed + outage; undefined for open / unknown without time.
 */
export const getVenueReopenWhenText = ({
    status,
    locale,
    t,
    closedKeys,
    outageWhenKeys,
}: {
    status: VenueAvailabilityStatus;
    locale: Locale;
    t: Translate;
    closedKeys: ClosedCopyKeys;
    outageWhenKeys: OutageWhenKeys;
}): string | undefined => {
    switch (status.kind) {
        case VenueAvailabilityKind.Closed:
            return getClosedCopyText(status, locale, t, closedKeys);
        case VenueAvailabilityKind.Outage:
            return getOutageReturnText(status.outageReturn, status.endsAtDayjs.toDate(), locale, t, {
                minutes: outageWhenKeys.minutes,
                soon: outageWhenKeys.soon,
                at: outageWhenKeys.today,
                tomorrowAt: outageWhenKeys.tomorrow,
                untilDate: outageWhenKeys.untilDate,
            });
        case VenueAvailabilityKind.Open:
        case VenueAvailabilityKind.ClosedUnknown:
            return undefined;
    }
};

/** Navbar / Outages prefab banner copy. Null when open. */
export const getVenueAvailabilityBannerMessage = ({
    status,
    locale,
    t,
    getLocalizedValue,
}: {
    status: VenueAvailabilityStatus;
    locale: Locale;
    t: Translate;
    getLocalizedValue: (value: LocalizedString) => string;
}): string | null => {
    switch (status.kind) {
        case VenueAvailabilityKind.Open:
            return null;
        case VenueAvailabilityKind.Closed:
            return getClosedCopyText(status, locale, t, {
                today: 'NAVBAR.CLOSED_OPENS_TODAY',
                tomorrow: 'NAVBAR.CLOSED_OPENS_TOMORROW',
                later: 'NAVBAR.CLOSED_OPENS_ON_DAY',
            });
        case VenueAvailabilityKind.ClosedUnknown:
            return t('NAVBAR.CLOSED_GENERIC');
        case VenueAvailabilityKind.Outage: {
            const description = getLocalizedValue(status.description ?? {});
            const tail = getOutageReturnText(status.outageReturn, status.endsAtDayjs.toDate(), locale, t, {
                minutes: 'NAVBAR.OUTAGE_TAIL_IN_MINUTES',
                soon: 'NAVBAR.OUTAGE_TAIL_SOON',
                at: 'NAVBAR.OUTAGE_TAIL_AT',
                tomorrowAt: 'NAVBAR.OUTAGE_TAIL_TOMORROW_AT',
                untilDate: 'NAVBAR.OUTAGE_TAIL_UNTIL_DATE',
            });
            return `${description || t('NAVBAR.OUTAGE_LEAD')} · ${tail}`;
        }
    }
};
